/**
 * **A plan task, answered where it stands** — the plan's cover, how the client actually executed it
 * (`GET /plan-assignment/{id}/execution-summary`: sessions done of prescribed, missed, attendance,
 * tonnage, records, and a strip week by week), and the one move each of the three kinds allows.
 *
 * ── The moves follow training-plan R14 ───────────────────────────────────────────────────────
 * A client may hold ONE active plan, and assigning over it is a 409 (`client_already_has_active_plan`).
 * So a plan that is ENDING offers „Dodaj tydzień", and closing it — alone or followed by the plan
 * library (owner, 25 September 2026); a plan whose schedule RAN OUT offers „Zakończ plan" and
 * „Zakończ plan i przypisz nowy" (complete, then `/plans`, where the next plan is chosen — the library
 * takes no client in its address, so the trainer lands on it plain); a self-paced plan that CLOSED
 * ITSELF offers „Przypisz następny" alone, which opens the page's next-plan dialog
 * (`features/create-plan`, the client already named). No card assigns over an active plan.
 *
 * ── The Przegląd's card: the cover beside the balance (owner, 25 September 2026) ─────────────
 * Its big figure went: the days to the end are the head's meta line („koniec 2 paź · za 7 dni"),
 * the cover stands on the left with the plan's category and name, and beside it the weeks and three
 * rows of the balance — sessions done of prescribed, missed, attendance. Its act row carries
 * „Dodaj tydzień" and „Zakończ plan" without the row's label, which only repeated the head.
 *
 * ── An older item has no id, and then no write ───────────────────────────────────────────────
 * `planAssignmentId` is an additive payload field (24 September 2026). An item from before it still
 * reads the client's active plan for the cover and the balance — for the two kinds whose plan is
 * still `Active` — and draws no write (`model/writeTargets`), rather than writing to a guessed
 * assignment.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { assignmentById, activeAssignment, customerAssignmentsQueryOptions, executionSummaryQueryOptions } from '~/entities/plan-assignment';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import photo from '~/shared/ui/photoBox.module.css';

import { useHomeFormats } from '../lib/useHomeFormats';
import type { TaskAct } from '../lib/useTaskAct';
import type { TaskDensity, TaskSlots, TaskWrites } from '../lib/taskWrites';
import type { AttentionItem } from '../model/attentionItems';
import { planExecution, type PlanExecution, type SlotMark } from '../model/planExecution';
import { taskWriteTargets, type TaskPlanTarget } from '../model/writeTargets';
import type { TaskHero } from '../model/taskContent';
import { ActButton, ActRefusal, ActRow, ActSpacer } from './TaskActs';
import { BlockRead } from './BlockRead';
import styles from './taskBlocks.module.css';

const STRIP: Readonly<Record<SlotMark, string | undefined>> = {
  done: undefined,
  missed: styles.stripMissed,
  ahead: styles.stripAhead,
  other: styles.stripOther,
};

const ADD_WEEK = 'addWeek';
const COMPLETE = 'complete';
const COMPLETE_AND_ASSIGN = 'completeAndAssign';

interface PlanBlockProps {
  readonly item: AttentionItem;
  readonly density: TaskDensity;
  /** The card's one figure, drawn on the cover. */
  readonly hero: TaskHero | null;
  readonly slots: TaskSlots;
  readonly act: TaskAct;
}

export function PlanBlock({ item, density, hero, slots, act }: PlanBlockProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contentLang = useContentLanguage();
  const customerId = item.subjectCustomerId;
  const assignments = useQuery(customerAssignmentsQueryOptions(customerId ?? 0, { enabled: customerId !== null }));
  const namedId = item.triage.planAssignmentId;
  const plan =
    namedId !== null
      ? assignmentById(assignments.data, namedId)
      : item.reason === 'planCompleted'
        ? null
        : activeAssignment(assignments.data);
  const assignmentId = namedId ?? plan?.id ?? null;
  const summary = useQuery(
    executionSummaryQueryOptions(assignmentId ?? 0, contentLang, { enabled: assignmentId !== null }),
  );
  const gate = useSkeletonGate(
    (customerId !== null && assignments.isPending) || (assignmentId !== null && summary.isPending),
  );
  const execution = summary.data === undefined ? null : planExecution(summary.data);
  // A failed read is not a plan with nothing to show: the balance says the read failed.
  const failed = assignments.isError || summary.isError;
  const name = plan?.name ?? item.triage.planName ?? '';
  const target = taskWriteTargets(item).plan;

  const writes = density === 'short' ? undefined : slots.writes;

  return (
    <div className={`${String(styles.stack)} ${String(styles[density])}`} aria-busy={gate.waiting}>
      <BlockRead
        gate={gate}
        reserve={density === 'desk' ? styles.reservePlanDesk : styles.reservePlanQuick}
        className={density === 'quick' ? styles.planQuick : styles.stack}
      >
        <div className={styles.cover}>
          {plan?.coverUrl == null ? null : (
            <img className={`${String(photo.photo)} ${String(styles.coverPhoto)}`} src={plan.coverUrl} alt="" decoding="async" />
          )}
          <span className={styles.coverShade} />
          <div className={styles.coverText}>
            <span className={styles.coverCategory}>
              {[
                plan?.category == null ? '' : t(`planTemplates.category.${plan.category}`, { defaultValue: plan.category }),
                plan?.schedulingMode == null ? '' : t(`web:tasks.blocks.plan.mode.${plan.schedulingMode}`, { defaultValue: '' }),
              ]
                .filter((part) => part !== '')
                .join(' · ')}
            </span>
            <span className={styles.coverName}>{name}</span>
          </div>
          {/* The desk's cover carries the figure; the Talia card folds it into its head's meta line. */}
          {hero === null || density !== 'desk' ? null : (
            <span className={styles.coverFigure}>
              <span>
                {hero.value}
                {hero.total === null ? null : <small className={styles.coverUnit}>{hero.total}</small>}
              </span>
              {hero.unit === null ? null : <small className={styles.coverUnit}>{hero.unit}</small>}
              <i>{hero.caption}</i>
            </span>
          )}
        </div>
        {failed ? (
          <p className={styles.note}>{t('web:tasks.blocks.readFailed')}</p>
        ) : execution === null ? null : density === 'quick' ? (
          <div className={`${String(styles.stack)} ${String(styles.planSide)}`}>
            <Weeks execution={execution} ended={item.reason !== 'planEndingSoon'} />
            <Rows execution={execution} />
          </div>
        ) : (
          <>
            <Weeks execution={execution} ended={item.reason !== 'planEndingSoon'} />
            {density === 'desk' ? <Balance execution={execution} /> : null}
          </>
        )}
      </BlockRead>

      {writes === undefined ? null : acts(writes)}
    </div>
  );

  function acts(writes: TaskWrites) {
    const assignNext = slots.assignNext;
    if (item.reason === 'planEndingSoon') {
      if (target === null) return null;
      return (
        <>
          <ActRow label={rowLabel(t('web:tasks.blocks.plan.endingLabel'))}>
            <ActSpacer />
            {closeButtons(writes, target, {
              complete: t('web:tasks.blocks.acts.endPlan'),
              // The Talia card's row has room for two acts: the week, and the close alone.
              completeAndAssign: density === 'desk' ? t('web:tasks.blocks.acts.endAndAssign') : null,
              primary: false,
            })}
            <ActButton
              controller={act}
              tone="ink"
              glyph="addWeek"
              label={t('web:tasks.blocks.acts.addWeek')}
              run={{
                act: ADD_WEEK,
                // NOT a close: „ending soon" is a seven-day window, so a plan with a week added can
                // still be inside it and the server still carry the item — and a struck card cannot
                // be reopened. The queue is invalidated; the next read strikes the card if the item
                // is gone (`reconcileDeck`).
                closes: false,
                done: t('web:tasks.blocks.live.weekAdded'),
                write: () => writes.addWeek(target),
              }}
            />
          </ActRow>
          <ActRefusal controller={act} acts={[ADD_WEEK, COMPLETE, COMPLETE_AND_ASSIGN]} />
        </>
      );
    }
    if (item.reason === 'planCompletionDue') {
      if (target === null) return null;
      return (
        <>
          <ActRow label={rowLabel(t('web:tasks.blocks.plan.dueLabel'))}>
            <ActSpacer />
            {closeButtons(writes, target, {
              complete: t('web:tasks.blocks.acts.completePlan'),
              completeAndAssign: t('web:tasks.blocks.acts.completeAndAssign'),
              primary: true,
            })}
          </ActRow>
          <ActRefusal controller={act} acts={[COMPLETE, COMPLETE_AND_ASSIGN]} />
        </>
      );
    }
    if (assignNext === undefined || customerId === null) return null;
    return (
      <ActRow label={rowLabel(t('web:tasks.blocks.plan.completedLabel'))}>
        <ActSpacer />
        <ActButton
          controller={act}
          tone="ink"
          glyph="assign"
          label={t('web:tasks.blocks.acts.assignNext')}
          onPress={() => {
            assignNext(item);
          }}
        />
      </ActRow>
    );
  }

  /** The row's label on the desk; the Talia card's head already names the kind. */
  function rowLabel(label: string): string | undefined {
    return density === 'desk' ? label : undefined;
  }

  /**
   * Closing the plan, alone or followed by the plan library — the same two writes on a plan
   * that is ENDING (owner, 25 September 2026: „Zakończ plan", „Zakończ plan i przypisz nowy") and on
   * one whose schedule ran out. Both close the card. The next plan is offered only AFTER the close,
   * because a client may hold one active plan (training-plan R14). `primary` says whether the pair
   * carries the card's one ink control: on the ending card that is „Dodaj tydzień".
   */
  function closeButtons(
    writes: TaskWrites,
    target: TaskPlanTarget,
    words: { readonly complete: string; readonly completeAndAssign: string | null; readonly primary: boolean },
  ) {
    const assignNext = slots.assignNext;
    return (
      <>
        <ActButton
          controller={act}
          tone={words.primary && assignNext === undefined ? 'ink' : 'ghost'}
          glyph="finish"
          label={words.complete}
          run={{
            act: COMPLETE,
            closes: true,
            done: t('web:tasks.blocks.live.planClosed'),
            write: () => writes.completePlan(target),
          }}
        />
        {assignNext === undefined || words.completeAndAssign === null ? null : (
          <ActButton
            controller={act}
            tone={words.primary ? 'ink' : 'ghost'}
            glyph="assign"
            label={words.completeAndAssign}
            run={{
              act: COMPLETE_AND_ASSIGN,
              closes: true,
              done: t('web:tasks.blocks.live.planClosed'),
              // Only once the plan has LEFT `Active` may the next be assigned (R14): the library is
              // opened after the close, never beside it (owner, 25 September 2026).
              write: async () => {
                await writes.completePlan(target);
                void navigate({ to: '/plans' });
              },
            }}
          />
        )}
      </>
    );
  }
}

function Weeks({ execution, ended }: { readonly execution: PlanExecution; readonly ended: boolean }) {
  const { t } = useTranslation();
  const total = Math.max(execution.weekTotal, 1);
  const current = ended ? total + 1 : (execution.currentWeek ?? 0);
  return (
    <div className={`${String(styles.stack)} ${String(styles.weeks)}`}>
      <div className={styles.weeksBar} style={{ '--weeks': String(total) } as CSSProperties} aria-hidden="true">
        {Array.from({ length: total }, (_, index) => (
          <i
            key={index}
            className={`${String(styles.weekSeg)} ${
              index + 1 < current ? String(styles.weekDone) : index + 1 === current ? String(styles.weekNow) : ''
            }`}
          />
        ))}
      </div>
      <p className={styles.weeksAxis}>
        <span>{t('web:tasks.blocks.plan.weekOne')}</span>
        <span>
          {ended || execution.currentWeek === null
            ? t('web:tasks.blocks.plan.weeksAll', { count: total })
            : t('web:tasks.blocks.plan.weekOf', { week: execution.currentWeek, total })}
        </span>
      </p>
    </div>
  );
}

/** The Przegląd's balance: three rows beside the cover — sessions, missed, attendance. */
function Rows({ execution }: { readonly execution: PlanExecution }) {
  const { t } = useTranslation();
  return (
    <dl className={styles.planRows}>
      <div className={styles.planRow}>
        <dt>{t('web:tasks.blocks.plan.rows.sessions')}</dt>
        <dd>
          {execution.done} {t('web:tasks.blocks.plan.of', { total: execution.prescribed })}
        </dd>
      </div>
      <div className={`${String(styles.planRow)} ${execution.missed > 0 ? String(styles.planRowBad) : ''}`}>
        <dt>{t('web:tasks.blocks.plan.rows.missed')}</dt>
        <dd>{execution.missed}</dd>
      </div>
      {execution.compliancePct === null ? null : (
        <div className={styles.planRow}>
          <dt>{t('web:tasks.blocks.plan.rows.compliance')}</dt>
          <dd>{execution.compliancePct}%</dd>
        </div>
      )}
    </dl>
  );
}

/** The desk's balance of the plan: the counts, and the strip week by week. */
function Balance({ execution }: { readonly execution: PlanExecution }) {
  const { t } = useTranslation();
  const tonnes = useHomeFormats().decimal(execution.tonnageKg / 1000);
  return (
    <div className={styles.stats}>
      <div className={styles.statFigures}>
        <span className={styles.stat}>
          <b>
            {execution.done}
            <small className={styles.statSmall}> {t('web:tasks.blocks.plan.of', { total: execution.prescribed })}</small>
          </b>
          {t('web:tasks.blocks.plan.done')}
        </span>
        <span className={`${String(styles.stat)} ${execution.missed > 0 ? String(styles.statBad) : ''}`}>
          <b>{execution.missed}</b>
          {t('web:tasks.blocks.plan.missed', { count: execution.missed })}
        </span>
        {execution.compliancePct === null ? null : (
          <span className={styles.stat}>
            <b>{execution.compliancePct}%</b>
            {t('web:tasks.blocks.plan.compliance')}
          </span>
        )}
        <span className={styles.stat}>
          <b>{t('web:tasks.blocks.plan.tonnes', { value: tonnes })}</b>
          {t('web:tasks.blocks.plan.tonnage')}
        </span>
        <span className={styles.stat}>
          <b>{execution.records}</b>
          {t('web:tasks.blocks.plan.records', { count: execution.records })}
        </span>
      </div>
      {execution.weeks.length > 0 ? (
        <div className={styles.strip} aria-hidden="true">
          {execution.weeks.map((week) => (
            <span className={styles.stripWeek} key={week.number}>
              {week.marks.map((mark, index) => (
                <i className={`${String(styles.stripMark)} ${STRIP[mark] ?? ''}`} key={index} />
              ))}
              <em className={styles.stripNumber}>{week.number}</em>
            </span>
          ))}
          {execution.left > 0 ? (
            <span className={styles.stripLeft}>{t('web:tasks.blocks.plan.left', { count: execution.left })}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
